import * as React from 'react';
import { type ComponentPropsWithRef, type ElementRef, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { useComposition } from '@/hooks/use-composition';
import { useHasProp } from '@/hooks/use-has-prop';
import { cn } from '@/lib/utils';

const iconVariants = cva('absolute', {
  variants: {
    size: {
      default: 'size-4',
    },
    side: {
      left: 'left-3',
      right: 'right-3',
    },
    icon: {
      true: 'left-1/2 -translate-x-1/2 h-6',
    },
  },
  defaultVariants: {
    size: 'default',
    side: 'left',
    icon: false,
  },
});

interface ButtonIconProps
  extends React.HTMLAttributes<HTMLOrSVGElement>,
    VariantProps<typeof iconVariants> {}

const ButtonIcon = forwardRef<HTMLSlotElement, ButtonIconProps>(
  ({ className, side, icon, ...props }, ref) => (
    <Slot
      data-icon
      ref={ref}
      className={cn(iconVariants({ side, icon }), className)}
      {...props}
    />
  )
);
ButtonIcon.displayName = 'ButtonIcon';

const buttonVariants = cva(
  'flex cursor-pointer min-h-fit text-wrap truncate items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
      left: {
        true: 'pl-10 relative',
      },
      right: {
        true: 'pr-10 relative',
      },
      icon: {
        true: 'h-8 w-8 !p-0 relative',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      left: false,
      right: false,
      icon: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, type = 'button', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={className}
        ref={ref}
        type={type}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

interface ButtonComposition {
  Icon: typeof ButtonIcon;
}

const Root = forwardRef<
  ElementRef<typeof Button>,
  ComponentPropsWithRef<typeof Button>
>(({ children: _children, className, size, variant, ...props }, ref) => {
  const [children, icons] = useComposition(_children, ButtonIcon.displayName);
  const hasLeftIcon = useHasProp<ButtonIconProps>(icons ?? [], 'side', 'left');
  const hasRightIcon = useHasProp<ButtonIconProps>(
    icons ?? [],
    'side',
    'right'
  );
  const isIcon = useHasProp<ButtonIconProps>(icons ?? [], 'icon', true);
  return (
    <Button
      ref={ref}
      className={cn(
        buttonVariants({
          left: hasLeftIcon,
          right: hasRightIcon,
          icon: isIcon,
          size,
          variant,
          className,
        })
      )}
      {...props}>
      {icons}
      {children}
    </Button>
  );
});

Root.displayName = 'Button';

const RootWithComposition: typeof Root & ButtonComposition = Object.assign(
  Root,
  {
    Icon: ButtonIcon,
  }
);

export { RootWithComposition as Button };
